import { QueryResult } from '@apollo/client';
import { useTranslation } from 'next-i18next';
import React, { useCallback } from 'react';
import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useFeatureToggle } from '@hotelplan/libs.feature-toggle';
import { FdrProductType } from '@hotelplan/supergraph-api';
import {
  B2BStaticGeoRecommendationGridItem,
  B2BStaticGeoRecommendationSliderItem,
} from 'components/domain/fdr-page-components/recommenders/geo/fdr-b2b-geo-recommendation-item';
import { FdrProductRecommender } from 'components/domain/fdr-page-components/recommenders/product/fdr-product-recommender';
import { IFdrProductRecommenderComponent } from 'components/domain/fdr-page-components/recommenders/product/fdr-product-recommender.types';
import useFdrPageComponentsFetch from 'components/domain/fdr-page-components/useFdrPageComponentsFetch';
import { ProductRecommendationView } from 'components/domain/recommendations/Recommendations.constants';
import { FeatureList } from 'config/featureList';
import {
  FdrGeoProductsDocument,
  FdrGeoProductsQuery,
  FdrGeoProductsQueryVariables,
} from 'schemas/query/geo/fdr-geo-products.generated';
import FdrGeoMap from './fdr-geo-map';
import { TFdrAdventureTravelGeoProduct } from './fdr-geo.types';
import {
  createGeoProductVariables,
  mapFdrAdventureTravelToRecommenderProduct,
  mapFdrGeoProductResponseToRecommenderProducts,
} from './fdr-geo.utils';
import { NavigationIds } from './navigation/fdr-geo-navigation.types';

const GEO_PRODUCTS_FONT_COLOR = '#000000';
const GEO_PRODUCTS_BG_COLOR = '#ffffff';
const GRID_VIEW_VARIANT_NAME = 'new';

interface IGeoProductsProps {
  id: string;
  geoProducts: TFdrAdventureTravelGeoProduct[];
  total: number;
  geoObject: string;
}

const FdrGeoProducts: React.FC<IGeoProductsProps> = ({
  id,
  geoProducts,
  total,
  geoObject,
}) => {
  const [t] = useTranslation(['common', 'geo']);
  const { channelType } = useAuthentication();
  const { getVariant } = useFeatureToggle();
  const variant = getVariant(FeatureList.geoProductRecommenderView);
  const { mobile } = useDeviceType();
  const isGridView = variant?.name == GRID_VIEW_VARIANT_NAME && !mobile;
  const isMapDisplayed =
    variant?.name == GRID_VIEW_VARIANT_NAME &&
    (id.includes('c-') || id.includes('r-'));

  const isB2B = channelType === AuthChannelType.B2B;

  const mapGeoProduct = useCallback(
    (product: TFdrAdventureTravelGeoProduct) => {
      return mapFdrAdventureTravelToRecommenderProduct(product, isB2B);
    },
    [isB2B]
  );

  const mapGeoProductResponse = useCallback(
    (
      response: QueryResult<
        FdrGeoProductsQuery,
        FdrGeoProductsQueryVariables
      >['data']
    ) => {
      return mapFdrGeoProductResponseToRecommenderProducts(response, isB2B);
    },
    [isB2B]
  );

  const componentData: IFdrProductRecommenderComponent = {
    items: geoProducts.map(mapGeoProduct),
    total,
    name: t('geo:products.title', { geoObject }),
    fontColor: GEO_PRODUCTS_FONT_COLOR,
    backgroundColor: GEO_PRODUCTS_BG_COLOR,
  };

  const mapVariables = useCallback(
    (page, perPage) => {
      return createGeoProductVariables(
        id,
        page,
        perPage,
        [FdrProductType.AdventureTravel],
        isB2B
      );
    },
    [id, isB2B]
  );

  const { fetchItems, loading } = useFdrPageComponentsFetch(
    FdrGeoProductsDocument,
    mapVariables,
    mapGeoProductResponse
  );

  const productMap = isMapDisplayed ? <FdrGeoMap id={id} /> : null;

  return (
    <FdrProductRecommender
      component={componentData}
      id={NavigationIds.PRODUCTS}
      loadMoreText={t('geo:products.loadMore')}
      view={isGridView ? ProductRecommendationView.GRID : undefined}
      sliderRecommendationItem={
        isB2B ? B2BStaticGeoRecommendationSliderItem : undefined
      }
      gridRecommendationItem={
        isB2B ? B2BStaticGeoRecommendationGridItem : undefined
      }
      fetchItems={fetchItems}
      loading={loading}
      sectionHeaderTitle={t('product.recommender.section.title')}
    >
      {productMap}
    </FdrProductRecommender>
  );
};

export default FdrGeoProducts;
